import React, { useCallback } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { Calculate as CalculateIcon } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { useCalculateReimbursementMutation } from '../../../../../services/CalculatorService';
import { useGetCentersQuery } from '../../../../../services/ConfigurationService';
import { 
  setIsCalculating, 
  setCalculationResult,
  selectCurrentEstimate,
  selectIsCalculating,
} from '../../../../../services/ContractService/contractEstimateSlice';
import { CalculateReimbursementRequest, ProcedureInputDto } from '../../../../../services/CalculatorService/types/calculatorTypes';

/**
 * Maps center type code to facility type for the calculator API.
 * Returns undefined for unknown types (will fail validation on backend).
 */
const mapCenterTypeToFacilityType = (centerType: string | undefined): string | undefined => {
  if (!centerType) return undefined;
  
  const mapping: Record<string, string> = {
    'ASC': 'ASC',
    'HOS': 'Hospital',
    'HOPD': 'HOPD'
  };
  
  return mapping[centerType.toUpperCase()];
};

export const CalculateButton: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const company = searchParams.get('company') || '';
  
  const estimate = useAppSelector(selectCurrentEstimate);
  const isCalculating = useAppSelector(selectIsCalculating);
  
  // Fetch centers to get full CenterType with centerType field
  const { data: centers = [] } = useGetCentersQuery(
    { company },
    { skip: !company }
  );
  
  const [calculateReimbursement] = useCalculateReimbursementMutation();

  // Find the selected center to get its centerType from the full CenterType data
  const selectedCenter = centers.find(c => c.id === estimate?.centerId);
  const facilityType = mapCenterTypeToFacilityType(selectedCenter?.centerType);

  // Validation checks
  const canCalculate = Boolean(
    estimate?.centerId &&
    estimate?.payerId &&
    estimate?.serviceDate &&
    estimate?.procedureCodes?.length > 0 &&
    facilityType // Must have a valid facility type mapping
  );

  const handleCalculateEstimate = useCallback(async () => {
    if (!estimate || !canCalculate || !facilityType) return;

    dispatch(setIsCalculating(true));

    try {
      // Transform procedures to API format
      const procedures: ProcedureInputDto[] = estimate.procedureCodes.map(proc => ({
        code: proc.code,
        procedureType: proc.procedureType || 'CPT',
        revenueCode: proc.revenueCode,
        units: proc.units,
        billedCharges: proc.billedCharges || 0,
        modifiers: proc.modifiers,
        implantCost: proc.implantCost,
        apcCode: proc.apcCode,
      }));

      // Calculate total billed charges
      const totalBilledCharges = procedures.reduce(
        (sum, proc) => sum + (proc.billedCharges * proc.units),
        0
      );

      // Build the request
      const request: CalculateReimbursementRequest = {
        centerId: estimate.centerId,
        payerId: estimate.payerId,
        dateOfService: estimate.serviceDate,
        stayType: estimate.stayType || 'OP',
        facilityType: facilityType,
        procedures: procedures,
        totalBilledCharges: totalBilledCharges,
      };

      console.log('Calculating reimbursement:', request);

      const result = await calculateReimbursement(request).unwrap();
      
      console.log('Calculation result:', result);
      dispatch(setCalculationResult(result));
      
    } catch (error) {
      console.error('Calculation failed:', error);
      // TODO: Add error notification
    } finally {
      dispatch(setIsCalculating(false));
    }
  }, [estimate, canCalculate, facilityType, dispatch, calculateReimbursement]);

  // Determine button tooltip for disabled state
  const getDisabledReason = (): string | undefined => {
    if (!estimate?.centerId) return 'Select a center';
    if (!estimate?.payerId) return 'Select a payer';
    if (!estimate?.serviceDate) return 'Enter a service date';
    if (!estimate?.procedureCodes?.length) return 'Add at least one procedure';
    if (!facilityType) return 'Unknown center type - cannot calculate';
    return undefined;
  };

  return (
    <Button
      variant="contained"
      startIcon={isCalculating ? <CircularProgress size={20} color="inherit" /> : <CalculateIcon />}
      onClick={handleCalculateEstimate}
      disabled={!canCalculate || isCalculating}
      title={getDisabledReason()}
      sx={{
        backgroundColor: '#162346',
        '&:hover': {
          backgroundColor: '#1a2a52',
        },
        '&:disabled': {
          backgroundColor: '#6b7280',
          color: '#d1d5db',
        },
        textTransform: 'none',
        fontWeight: 500,
        px: 3
      }}
    >
      {isCalculating ? 'Calculating...' : 'Calculate'}
    </Button>
  );
};